import React, { useState, useEffect, useCallback } from 'react';
import { 
  surveyService, 
  SurveyDashboard, 
  SectionSummaryList, 
  SurveyFilters,
  ProblemSection 
} from '../../services/surveyService';
import SurveyDashboardComponent from './SurveyDashboard';
import SectionSummaryTable from './SectionSummaryTable';
import SectionDetailModal from './SectionDetailModal';
import ProblemSectionsCard from './ProblemSectionsCard';

const SurveyDataPage: React.FC = () => {
  const [dashboard, setDashboard] = useState<SurveyDashboard | null>(null);
  const [sectionSummaries, setSectionSummaries] = useState<SectionSummaryList | null>(null);
  const [problemSections, setProblemSections] = useState<ProblemSection[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SurveyFilters>({
    page: 1,
    pageSize: 50,
    sortBy: 'ResponseCount',
    descending: true
  });

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [dashboardData, problemData] = await Promise.all([
        surveyService.getDashboard(),
        surveyService.getProblemSections()
      ]);
      
      setDashboard(dashboardData);
      setProblemSections(problemData);
    } catch (err) {
      setError('Failed to load survey data');
      console.error('Error loading survey data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSectionSummaries = useCallback(async () => {
    try {
      const data = await surveyService.getSectionSummaries(filters);
      setSectionSummaries(data);
    } catch (err) {
      console.error('Error loading section summaries:', err);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadSectionSummaries();
  }, [loadSectionSummaries]);

  const handleFilterChange = (newFilters: Partial<SurveyFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading survey data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-red-800 font-semibold">Error Loading Data</h3>
            <p className="text-red-600 mt-2">{error}</p>
            <button 
              onClick={loadData}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Trail Section Survey Data</h1>
          <p className="mt-2 text-gray-600">
            Analyze user feedback and survey responses to improve trail time estimates and identify problem sections
          </p>
        </div>

        {dashboard && (
          <SurveyDashboardComponent dashboard={dashboard} />
        )}

        {problemSections.length > 0 && (
          <div className="mt-8">
            <ProblemSectionsCard 
              sections={problemSections}
              onSectionClick={setSelectedSectionId}
            />
          </div>
        )}

        {sectionSummaries && (
          <div className="mt-8">
            <SectionSummaryTable
              data={sectionSummaries}
              filters={filters}
              onFilterChange={handleFilterChange}
              onSectionClick={setSelectedSectionId}
            />
          </div>
        )}

        {selectedSectionId && (
          <SectionDetailModal
            gisId={selectedSectionId}
            onClose={() => setSelectedSectionId(null)}
          />
        )}
      </div>
    </div>
  );
};

export default SurveyDataPage;